import { FC, ReactNode, createContext } from 'react'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import {
  ExamTests_ExamTestFragment,
  PredictionOfPassedContainer_ExamGradingResultDistributionFragment,
  PredictionOfPassedContainer_ExamGradingResultFragment,
  PredictionOfPassedContainer_ExamReserveInformationFragment,
  SameExamInfoType,
  useGqlExamGradingRankFromTestLazyQuery,
  useGqlExamGradingRankOfSameInfoFromTestLazyQuery,
  useGqlExamGradingResultDistributionAllDataLazyQuery,
  useGqlExamGradingResultLazyQuery,
  useGqlExamTestsQuery,
  useGqlViewerExamReserveInformationInResultQuery,
} from 'generated/graphql'
import DEFAULT_TEST_ID from '@app.feature/exam/gsat2024-2/constants/defaultTestId'
import { SPREAD_SCORE_INFO_REQUEST_INPUT_SCORE_RANGES } from '../constants/stepFive.constant'

gql`
  fragment PredictionOfPassedContainer_examReserveInformation on ExamReserveInformation {
    ...TestUserInfoArticle_examReserveInformation
    hasAgreedToPrivacy
  }

  fragment PredictionOfPassedContainer_examGradingResult on ExamGradingResult {
    allExamGradingResultOfSameInfo: examGradingResultOfSameInfo(type: $type) {
      answeredCount
      correctCount
      correctRate
      detail {
        answeredCount
        correctCount
        correctRate
        subject
        totalCount
      }
    }
    subsidiaryJobExamGradingResultOfSameInfo: examGradingResultOfSameInfo(
      type: $subsidiaryJobExamGradingResultOfSameInfoType
    ) {
      answeredCount
      correctCount
      correctRate
      detail {
        answeredCount
        correctCount
        correctRate
        subject
        totalCount
      }
    }
    averageScore: examGradingResultOfSameInfoFromTest(type: $averageScoreType) {
      answeredCount
      correctRate
      correctCount
    }
    sameInfoAverage: examGradingResultOfSameInfoFromTest(
      type: $sameInfoAverageType
    ) {
      answeredCount
      correctRate
      correctCount
    }
    overallExamGradingResult {
      answeredCount
      correctCount
      correctRate
      detail {
        answeredCount
        correctCount
        correctRate
        subject
        totalCount
      }
    }
    subsidiaryJobtop30percentExamGradingResultOfSameInfo: top30percentExamGradingResultOfSameInfo(
      type: $top30PercentExamGradingResultOfSameInfoType2
    ) {
      answeredCount
      correctCount
      correctRate
      detail {
        answeredCount
        correctCount
        correctRate
        subject
        totalCount
      }
    }
    top30percentOfOverallExamGradingResult {
      answeredCount
      correctCount
      correctRate
      detail {
        answeredCount
        correctCount
        correctRate
        subject
        totalCount
      }
    }
    subsidiaryJobViewerRankOfSameInfo: viewerRankOfSameInfo(
      type: $subsidiaryJobViewerRankOfSameInfoType
    )
    ...SynthesisScoreInfoArticle_examGradingResult
  }

  fragment PredictionOfPassedContainer_examGradingResultDistribution on ExamGradingResultDistribution {
    range
    rate
  }
`

export interface IFPredictionOfPassedContainerContextProviderProps {
  children: ReactNode
  companyId: string
  termId: string
}

interface IFPredictionOfPassedContainerContextProps {
  examReserveInformation?: PredictionOfPassedContainer_ExamReserveInformationFragment | null
  examGradingResultData?: PredictionOfPassedContainer_ExamGradingResultFragment | null
  examTestsData?: Array<ExamTests_ExamTestFragment>
  examGradingResultDistribution?: Array<PredictionOfPassedContainer_ExamGradingResultDistributionFragment>
  examGradingResultDistributionSameInfo?: Array<PredictionOfPassedContainer_ExamGradingResultDistributionFragment>
  examGradingRankFromTestRate?: number
  examGradingRankOfSameInfoFromTestRate?: number
  isLoading: boolean
  companyId: string
  termId: string
}

const PredictionOfPassedContainerContext =
  createContext<IFPredictionOfPassedContainerContextProps>({
    isLoading: false,
    companyId: '',
    termId: '',
  })

const PredictionOfPassedContainerContextProvider: FC<
  IFPredictionOfPassedContainerContextProviderProps
> = ({ children, companyId, termId }) => {
  const router = useRouter()
  const shouldFetchRankData = router.pathname !== '/prediction/gsat2024-2'
  const [
    examGradingResult,
    { data: examGradingResultData, loading: gradingResultLoading },
  ] = useGqlExamGradingResultLazyQuery()

  const [examGradingRankFromTest, { data: examGradingRankFromTestRate }] =
    useGqlExamGradingRankFromTestLazyQuery({
      fetchPolicy: 'no-cache',
    })

  const [
    examGradingRankOfSameInfoFromTest,
    { data: examGradingRankOfSameInfoFromTestRate },
  ] = useGqlExamGradingRankOfSameInfoFromTestLazyQuery({
    fetchPolicy: 'no-cache',
  })

  const [
    examGradingResultDistributionAll,
    {
      data: examGradingResultDistributionAllData,
      loading: examGradingResultDistributionAllLoading,
    },
  ] = useGqlExamGradingResultDistributionAllDataLazyQuery()

  const {
    data: examReserveInformationData,
    loading: reserveInfomationLoading,
  } = useGqlViewerExamReserveInformationInResultQuery({
    variables: {
      input: {
        companyId,
        termId,
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      examGradingResult({
        variables: {
          testId:
            data.viewerExamReserveInformation?.examTest.id || DEFAULT_TEST_ID,
          subsidiaryJobExamGradingResultOfSameInfoType:
            SameExamInfoType.SUBSIDIARY_JOB,
          type: SameExamInfoType.ALL,
          viewerRankOfSameInfoType2: SameExamInfoType.SUBSIDIARY_JOB,
          top30PercentExamGradingResultOfSameInfoType2:
            SameExamInfoType.SUBSIDIARY_JOB,
          averageScoreType: SameExamInfoType.ALL,
          sameInfoAverageType: SameExamInfoType.SUBSIDIARY_JOB,
          subsidiaryJobViewerRankOfSameInfoType:
            SameExamInfoType.SUBSIDIARY_JOB,
        },
        fetchPolicy: 'network-only',
      })

      examGradingResultDistributionAll({
        variables: {
          inputAllInfo: {
            scoreRanges: SPREAD_SCORE_INFO_REQUEST_INPUT_SCORE_RANGES,
            testId:
              data.viewerExamReserveInformation?.examTest.id || DEFAULT_TEST_ID,
          },
          inputSameInfo: {
            scoreRanges: SPREAD_SCORE_INFO_REQUEST_INPUT_SCORE_RANGES,
            testId:
              data.viewerExamReserveInformation?.examTest.id || DEFAULT_TEST_ID,
          },
        },
        fetchPolicy: 'network-only',
      })

      if (shouldFetchRankData) {
        examGradingRankFromTest({
          variables: {
            testId:
              data.viewerExamReserveInformation?.examTest.id || DEFAULT_TEST_ID,
          },
        })

        examGradingRankOfSameInfoFromTest({
          variables: {
            testId:
              data.viewerExamReserveInformation?.examTest.id || DEFAULT_TEST_ID,
          },
        })
      }
    },
  })

  const { data: examTestsData, loading: examTestLoading } =
    useGqlExamTestsQuery({
      variables: {
        input: {
          companyId,
          termId,
        },
      },
    })

  const initialValue: IFPredictionOfPassedContainerContextProps = {
    examReserveInformation:
      examReserveInformationData?.viewerExamReserveInformation,
    examGradingResultData: examGradingResultData?.examGradingResult,
    examTestsData: examTestsData?.examTests,
    examGradingResultDistribution:
      examGradingResultDistributionAllData?.examGradingResultDistribution,
    examGradingResultDistributionSameInfo:
      examGradingResultDistributionAllData?.examGradingResultDistributionSameInfo,
    examGradingRankFromTestRate:
      examGradingRankFromTestRate?.examGradingRankFromTest ??
      examGradingResultData?.examGradingResult?.viewerRank,
    examGradingRankOfSameInfoFromTestRate:
      examGradingRankOfSameInfoFromTestRate?.examGradingRankOfSameInfoFromTest ??
      examGradingResultData?.examGradingResult?.viewerRankOfSameInfo,
    isLoading:
      reserveInfomationLoading ||
      gradingResultLoading ||
      examTestLoading ||
      examGradingResultDistributionAllLoading,
    companyId,
    termId,
  }

  return (
    <PredictionOfPassedContainerContext.Provider value={initialValue}>
      {children}
    </PredictionOfPassedContainerContext.Provider>
  )
}

export {
  PredictionOfPassedContainerContext,
  PredictionOfPassedContainerContextProvider,
}
